import { Injectable } from '@angular/core';
import { SentryErrorHandler } from '@core/services/common';
import {
  SecureStorage,
  SecureStorageObject
} from '@ionic-native/secure-storage/ngx';
import { Platform } from '@ionic/angular';
import { LogService } from '../log.service';

export enum KeychainKeys {
  PASSWORD
}

@Injectable({
  providedIn: 'root'
})
export class KeychainService {
  private readonly APP_NAMESPACE = 'com.mapfre.mapfreapp';
  private storageObj: SecureStorageObject;

  constructor(
    private secureStorage: SecureStorage,
    private log: LogService,
    private platform: Platform
  ) {}

  async isKeychainAvailable() {
    await this.init();
    this.log.d(this, 'Initialization: keychain is ready!');
    const isKeychainAvailable = this.storageObj != null;

    return isKeychainAvailable;
  }

  async get(key: keyof typeof KeychainKeys) {
    await this.init();

    const isKeychainAvailable = await this.isKeychainAvailable();
    if (!isKeychainAvailable) {
      throw new Error('Secured storage not enabled');
    }

    return this.storageObj.get(key);
  }

  async set(key: keyof typeof KeychainKeys, value: string) {
    await this.init();

    const isKeychainAvailable = await this.isKeychainAvailable();
    if (!isKeychainAvailable) {
      throw new Error('Secured storage not enabled');
    }

    await this.storageObj.remove(key).catch(() => {});

    return this.storageObj.set(key, value).catch(err => {
      if (this.platform.is('hybrid')) {
        SentryErrorHandler.sendCustomMessage(`Error saving ${key}: ${err}`, 'warning');
      }
      throw err;
    });
  }

  private async init() {
    if (!this.platform.is('cordova')) { return; }
    if (this.storageObj != null) { return; }

    try {
      this.storageObj = await this.secureStorage.create(this.APP_NAMESPACE);
    } catch (err) {
      this.log.w(this, `Secured storage couldn't be initialized. Err`, err);
    }
  }
}
